/**
 * Product 3D Carousel
 * CSS 3D ring with all items visible. Back items fade & shrink via perspective.
 */
(function () {
  'use strict';

  var INSTANCE_KEY = '_pc3d';

  function ProductCarousel3D(el) {
    this.el = el;
    this.ring = el.querySelector('[data-carousel-ring]');
    this.cards = Array.from(el.querySelectorAll('[data-carousel-card]'));
    this.dots = Array.from(el.querySelectorAll('[data-carousel-dot]'));
    this.prevBtn = el.querySelector('[data-carousel-prev]');
    this.nextBtn = el.querySelector('[data-carousel-next]');
    this.scene = el.querySelector('[data-carousel-scene]');
    this.infoPanels = Array.from(el.querySelectorAll('[data-info-panel]'));
    this.bars = Array.from(el.querySelectorAll('[data-bar]'));
    this.barValues = Array.from(el.querySelectorAll('[data-bar-value]'));

    this.thumbs = Array.from(el.querySelectorAll('[data-carousel-thumb]'));
    this.infoInner = el.querySelector('[data-info-inner]');
    this.mobileTitleEl = el.querySelector('[data-mobile-product-title]');
    this.mobileUspsEl = el.querySelector('[data-mobile-usps]');
    this.mobilePriceEl = el.querySelector('[data-mobile-price]');

    // Parse ratings data from JSON
    var ratingsEl = el.querySelector('[data-carousel-ratings]');
    this.ratings = ratingsEl ? JSON.parse(ratingsEl.textContent) : [];
    this.barKeys = ['cafeine', 'pomp', 'tintelingen', 'stimulanten'];

    // Section-level caffeine-free mode toggle
    this.modeToggleEl = el.querySelector('[data-mode-toggle]');
    this.modePills = Array.from(el.querySelectorAll('[data-mode-toggle] [data-mode]'));

    // Track all cards as "all" and the visible subset based on cfMode.
    // In CF-mode, only cards with data-has-cf="true" are rendered in the ring.
    this.allCards = this.cards.slice();
    this.cfMode = false;
    this.visibleCards = this.allCards.slice();

    this.count = this.visibleCards.length;
    if (this.count < 2) return;

    this.currentIndex = 0;
    this.angle = 360 / this.count;
    this.autoplay = el.dataset.carouselAutoplay === 'true';
    this.autoplayDelay = parseInt(el.dataset.carouselDelay, 10) || 4000;
    this.autoplayTimer = null;

    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.currentRotation = 0;
    this.dragRotation = 0;

    // Honour reduced-motion preference. When set, autoplay won't run and the
    // drag-follow translateX on the price chip is skipped.
    this.reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Bound handlers
    this._onMouseMove = this._onDragMove.bind(this);
    this._onMouseUp = this._onDragEnd.bind(this);
    this._onTouchMove = this._onDragMove.bind(this);
    this._onTouchEnd = this._onDragEnd.bind(this);

    // Debounced resize handler — recompute radius and reapply transforms when
    // the viewport changes (rotation, devtools toggle, browser zoom).
    this._resizeTimer = null;
    this._onResize = function () {
      if (this._resizeTimer) clearTimeout(this._resizeTimer);
      var self = this;
      this._resizeTimer = setTimeout(function () {
        // Skip if the user is mid-drag or mid-mode-transition; the geometry
        // will be reapplied when those flows settle.
        if (self.isDragging || self._modeTransitionInFlight) return;
        var prevRadius = self.radius;
        self.radius = self._calcRadius();
        if (self.radius !== prevRadius) {
          self.ring.style.transform =
            'translateZ(-' + self.radius + 'px) rotateY(' + self.currentRotation + 'deg)';
          self._updateCardTransforms(self.currentRotation);
        }
      }, 150);
    }.bind(this);

    this._init();
  }

  /**
   * Find the visible-cards index matching the current product page.
   * Returns 0 if no match or not on a product page. Operates on `visibleCards`
   * because that's what the ring renders against in both regular and CF mode.
   */
  ProductCarousel3D.prototype._findCurrentProductIndex = function () {
    var currentId = this.el.dataset.currentProduct;
    if (!currentId) return 0;

    for (var i = 0; i < this.visibleCards.length; i++) {
      if (this.visibleCards[i].dataset.productId === currentId) return i;
    }
    return 0;
  };

  ProductCarousel3D.prototype._calcRadius = function () {
    var configRadius = parseInt(this.el.dataset.carouselRadius, 10) || 0;
    if (configRadius > 0) return configRadius;

    var sample = (this.visibleCards && this.visibleCards[0]) || this.cards[0];
    var cardWidth = sample ? sample.offsetWidth : 0;
    if (!cardWidth || cardWidth < 50) cardWidth = 450;
    var minCount = Math.max(this.count, 3);

    // Spacing factor controls how spread-out the ring is (lower = more compact).
    // Driven by the `--pc3d-card-spacing` CSS variable on the scene element so
    // the value can be tuned per breakpoint via the stylesheet.
    var spacing = 0.88;
    if (this.scene) {
      var raw = parseFloat(getComputedStyle(this.scene).getPropertyValue('--pc3d-card-spacing'));
      if (!isNaN(raw) && raw > 0) spacing = raw;
    }

    // Special case for 2 cards (CF mode) — pair them up at a 90° angle with a
    // radius tuned so both sit clearly side-by-side without overlap, instead of
    // the default formula which would put them on top of each other.
    if (this.count === 2) {
      return Math.round(cardWidth * 0.8);
    }

    return Math.round((cardWidth * spacing) / Math.tan(Math.PI / minCount));
  };

  ProductCarousel3D.prototype._init = function () {
    this.radius = this._calcRadius();

    // Set initial info panel height
    if (this.infoInner && this.infoPanels[0]) {
      this.infoInner.style.height = this.infoPanels[0].scrollHeight + 'px';
    }

    // Pre-select current product if on a product page
    var startIndex = this._findCurrentProductIndex();
    this._rotate(startIndex);
    this._bindEvents();
    this._bindQuickView();
    this._bindModeToggle();
    if (this.autoplay) this._startAutoplay();
  };

  /**
   * Bind Quick View triggers with eager pre-fetching.
   *
   * The product cards elsewhere in the theme pre-warm the Quick View fetch on
   * `mouseenter` so the modal opens instantly on click. We mirror that pattern
   * for the carousel CTA: pre-fetch on `mouseenter` / `focus` / `touchstart`
   * (one-shot per button via `dataset.prefetched`), then on `click` only call
   * the open+render method — the network request is already in flight.
   *
   * Falls back to a direct PDP navigation when the Quick View element isn't
   * present (e.g. unrelated theme template), so the user is never stuck.
   */
  ProductCarousel3D.prototype._bindQuickView = function () {
    var buttons = this.el.querySelectorAll('[data-pc3d-quick-view]');
    buttons.forEach(function (btn) {
      var prefetch = function () {
        if (btn.dataset.prefetched === 'true') return;
        var url = btn.dataset.productUrl;
        var id = btn.dataset.productId;
        if (!url) return;
        var quickView = document.getElementById('Quick-view');
        if (!quickView || !quickView.requestProductFromUrl) return;
        try {
          quickView.requestProductFromUrl(url, id);
          btn.dataset.prefetched = 'true';
        } catch (_) { /* fail silent; the click path will still work */ }
      };

      // Pre-warm on hover / focus / first touch
      btn.addEventListener('mouseenter', prefetch);
      btn.addEventListener('focus', prefetch);
      btn.addEventListener('touchstart', prefetch, { passive: true });

      btn.addEventListener('click', function () {
        var url = btn.dataset.productUrl;
        var id = btn.dataset.productId;
        if (!url) return;
        var quickView = document.getElementById('Quick-view');

        // Defensive fallback: no quick-view component → go to the PDP directly
        if (!quickView || !quickView.openAndRenderProductByUrl) {
          window.location.assign(url);
          return;
        }

        // Make sure the fetch has started even if the user clicked without hovering
        if (quickView.requestProductFromUrl && btn.dataset.prefetched !== 'true') {
          try { quickView.requestProductFromUrl(url, id); } catch (_) {}
          btn.dataset.prefetched = 'true';
        }

        // Visual loading state — class is removed once the modal finishes rendering
        btn.classList.add('pc3d-info-panel__cta--loading');
        var clearLoading = function () {
          btn.classList.remove('pc3d-info-panel__cta--loading');
        };

        var maybePromise = quickView.openAndRenderProductByUrl(url);
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise.then(clearLoading, clearLoading);
        } else {
          // Method returns void in some implementations — clear after a short tick
          setTimeout(clearLoading, 600);
        }
      });
    });
  };

  /**
   * Bind the section-level caffeine mode toggle. Clicking a pill flips the
   * whole section between regular and CF mode (replaces the old per-product
   * inline toggle).
   */
  ProductCarousel3D.prototype._bindModeToggle = function () {
    if (!this.modeToggleEl) return;
    var self = this;
    this.modePills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var requested = pill.dataset.mode === 'cf';
        if (requested === self.cfMode) return;
        self._setCfMode(requested);
      });
    });
  };

  /**
   * Resolve the swap target for a card or thumb based on the current cf mode.
   * Returns null when the element has no eligible alternative source.
   */
  ProductCarousel3D.prototype._resolveSwapTarget = function (el, useCf, kind) {
    if (!el) return null;
    var src = useCf
      ? (kind === 'thumb' ? el.dataset.cfThumb : el.dataset.cfImage)
      : (kind === 'thumb' ? el.dataset.regularThumb : el.dataset.regularImage);
    if (!src) return null;
    var img = el.querySelector('img');
    if (!img) return null;
    return { img: img, src: src };
  };

  /**
   * Rebuild the info panel content for a given block index from the data island,
   * picking regular or CF source based on the section's current cfMode.
   */
  ProductCarousel3D.prototype._refreshInfoPanel = function (origIdx) {
    var data = this.ratings[origIdx];
    if (!data) return;
    var source = (this.cfMode && data.cf) ? data.cf : data.regular;
    var panel = this.infoPanels[origIdx];
    if (!panel || !source) return;

    var titleEl = panel.querySelector('[data-cf-title]');
    var subtitleEl = panel.querySelector('[data-cf-subtitle]');
    var descEl = panel.querySelector('[data-cf-desc]');
    if (titleEl) titleEl.textContent = source.title || '';
    if (subtitleEl) {
      subtitleEl.textContent = source.subtitle || '';
      subtitleEl.style.display = source.subtitle ? '' : 'none';
    }
    if (descEl) {
      descEl.textContent = source.description || '';
      descEl.style.display = source.description ? '' : 'none';
    }

    var priceEl = panel.querySelector('[data-cf-price]');
    if (priceEl && source.price) {
      var priceHtml = '';
      var onSale = !!source.compare_at_price;
      if (onSale) {
        priceHtml += '<span class="pc3d-info-panel__price-compare">' + source.compare_at_price + '</span>';
      }
      if (source.price_varies) {
        priceHtml += '<span class="pc3d-info-panel__price-prefix">Vanaf</span>';
      }
      priceHtml += '<span class="pc3d-info-panel__price-current'
        + (onSale ? ' pc3d-info-panel__price-current--sale' : '')
        + '">' + source.price + '</span>';
      priceEl.innerHTML = priceHtml;
    }

    var ctaBtn = panel.querySelector('[data-pc3d-quick-view]');
    if (ctaBtn) {
      var prevUrl = ctaBtn.dataset.productUrl;
      ctaBtn.dataset.productUrl = source.url || '';
      ctaBtn.dataset.productId = source.id || '';
      // Reset the prefetched flag if the URL actually changed, so the next
      // hover/touch warms the new product instead of relying on the old fetch.
      if (prevUrl !== ctaBtn.dataset.productUrl) {
        delete ctaBtn.dataset.prefetched;
      }
    }
  };

  /**
   * Switch the section between regular and caffeine-free mode in three phases:
   *   1. Fade EVERY product image (cards + thumbs) to opacity 0 in unison.
   *   2. Once fade-out is done AND every new image is preloaded: apply the
   *      mode change (class, geometry, src swap, info panels, ring snap) all
   *      at once, while still invisible.
   *   3. Fade EVERY product image back to opacity 1 together. Cards/thumbs
   *      that are now hidden by the `is-mode-cf` rule stay invisible because
   *      that CSS rule sets their CARD-level opacity to 0 (which beats the
   *      img opacity).
   *
   * Result: every product image moves in/out of the swap as one synchronised
   * unit — no staggered flicker between CF-eligible and non-CF cards.
   */
  ProductCarousel3D.prototype._setCfMode = function (cfMode) {
    if (cfMode === this.cfMode) return;
    if (this._modeTransitionInFlight) return; // ignore rapid re-clicks mid-transition
    this._modeTransitionInFlight = true;

    var self = this;
    var newMode = !!cfMode;
    var fadeMs = 450;

    // Toggle pill UI flips immediately — independent visual feedback for the click
    if (this.modePills) {
      var requestedMode = newMode ? 'cf' : 'regular';
      this.modePills.forEach(function (p) {
        var active = p.dataset.mode === requestedMode;
        p.classList.toggle('is-active', active);
        p.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // Collect every card + thumb img so we can fade them all together
    var allImgs = [];
    this.allCards.forEach(function (c) {
      var img = c.querySelector('img');
      if (img) allImgs.push(img);
    });
    this.thumbs.forEach(function (t) {
      var img = t.querySelector('img');
      if (img) allImgs.push(img);
    });

    // CF-eligible swap targets (cards + thumbs that need a different src in the new mode)
    var swapTargets = [];
    this.allCards.forEach(function (c) {
      if (c.dataset.hasCf === 'true') {
        var t = self._resolveSwapTarget(c, newMode, 'card');
        if (t) swapTargets.push(t);
      }
    });
    this.thumbs.forEach(function (t) {
      if (t.dataset.hasCf === 'true') {
        var swap = self._resolveSwapTarget(t, newMode, 'thumb');
        if (swap) swapTargets.push(swap);
      }
    });

    // ── Phase 1: fade all imgs out simultaneously ──
    allImgs.forEach(function (img) { img.style.opacity = '0'; });

    // ── Coordinate Phase 2 once BOTH fade-out is done AND preloads complete ──
    var fadeOutDone = false;
    var preloadDone = swapTargets.length === 0;

    function applyModeChange() {
      // Swap srcs in one batch
      swapTargets.forEach(function (t) {
        t.img.removeAttribute('srcset');
        t.img.removeAttribute('data-srcset');
        t.img.src = t.src;
      });

      self.cfMode = newMode;
      self.el.classList.toggle('is-mode-cf', newMode);

      // Rebuild visible cards based on new mode and recompute ring geometry
      self.visibleCards = self.allCards.filter(function (c) {
        return !self.cfMode || c.dataset.hasCf === 'true';
      });
      self.count = self.visibleCards.length;
      self.angle = self.count > 0 ? 360 / self.count : 0;
      // For 2 cards, override angle to 90° so the second card sits clearly to the
      // side instead of behind (default 360/2 = 180° = directly opposite = invisible).
      if (self.count === 2) self.angle = 90;
      self.radius = self._calcRadius();

      // Refresh CF-eligible info panels so their content matches the active source
      for (var p = 0; p < self.infoPanels.length; p++) {
        if (self.infoPanels[p].dataset.hasCf === 'true') {
          self._refreshInfoPanel(p);
        }
      }

      // Snap to a visible product if current one was filtered out
      var currentCard = self.allCards[self.currentIndex];
      var stillVisible = currentCard && self.visibleCards.indexOf(currentCard) !== -1;
      var targetOrig = stillVisible
        ? self.currentIndex
        : (self.visibleCards[0] ? self.allCards.indexOf(self.visibleCards[0]) : 0);

      // Snap ring + cards without animating through cumulative rotation
      var ring = self.ring;
      var prevRingTransition = ring.style.transition;
      ring.style.transition = 'none';
      // Also disable per-card transitions for this snap so the depth opacity
      // doesn't conflict with the orchestrated img fade
      var prevCardTransitions = self.allCards.map(function (c) {
        var prev = c.style.transition;
        c.style.transition = 'none';
        return prev;
      });

      self._initialised = false;
      self._rotate(targetOrig);

      void ring.offsetWidth;
      ring.style.transition = prevRingTransition;
      self.allCards.forEach(function (c, idx) {
        c.style.transition = prevCardTransitions[idx];
      });

      // ── Phase 3: fade everyone back in. Cards now hidden by is-mode-cf
      //    keep their CARD-level opacity 0 (CSS rule), so even with img
      //    opacity 1 they stay invisible. ──
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          allImgs.forEach(function (img) { img.style.opacity = '1'; });
          // Release the in-flight guard once the fade-in transition completes
          setTimeout(function () { self._modeTransitionInFlight = false; }, fadeMs);
        });
      });
    }

    function maybeApply() {
      if (fadeOutDone && preloadDone) applyModeChange();
    }

    // Track the fade-out timer so destroy() can cancel any pending mode swap
    this._modeFadeOutTimer = setTimeout(function () {
      self._modeFadeOutTimer = null;
      fadeOutDone = true;
      maybeApply();
    }, fadeMs);

    if (swapTargets.length) {
      var loaded = 0;
      swapTargets.forEach(function (t) {
        var preloader = new Image();
        var settle = function () {
          loaded++;
          if (loaded === swapTargets.length) {
            preloadDone = true;
            maybeApply();
          }
        };
        preloader.onload = settle;
        preloader.onerror = settle;
        preloader.src = t.src;
      });
    }
  };

  /**
   * Re-trigger a CSS fade-up animation on an element by toggling `is-fading`.
   * Forces a reflow between remove + add so the animation always restarts,
   * matching the 0.8s ease used by the info panel transition.
   */
  ProductCarousel3D.prototype._triggerFade = function (el) {
    if (!el) return;
    el.classList.remove('is-fading');
    void el.offsetWidth;
    el.classList.add('is-fading');
  };

  /**
   * Update the mobile-only product title shown above the stats bars.
   * Reflects the active product and its current cf-toggle state.
   */
  ProductCarousel3D.prototype._updateMobileTitle = function (idx) {
    if (!this.mobileTitleEl) return;
    var data = this.ratings[idx];
    if (!data) return;
    var source = (this.cfMode && data.cf) ? data.cf : data.regular;
    if (source && source.title) this.mobileTitleEl.textContent = source.title;
    this._triggerFade(this.mobileTitleEl);
  };

  /**
   * Update the mobile-only floating price chip above the active product image.
   * Mirrors the structure used in the info panel (compare-at + vanaf prefix + current).
   */
  ProductCarousel3D.prototype._updateMobilePrice = function (idx) {
    if (!this.mobilePriceEl) return;
    var data = this.ratings[idx];
    if (!data) return;
    var source = (this.cfMode && data.cf) ? data.cf : data.regular;
    if (!source || !source.price) {
      this.mobilePriceEl.innerHTML = '';
      return;
    }
    var html = '';
    var onSale = !!source.compare_at_price;
    if (onSale) {
      html += '<span class="pc3d-carousel-price__compare">' + source.compare_at_price + '</span>';
    }
    if (source.price_varies) {
      html += '<span class="pc3d-carousel-price__prefix">Vanaf</span>';
    }
    html += '<span class="pc3d-carousel-price__current'
      + (onSale ? ' pc3d-carousel-price__current--sale' : '')
      + '">' + source.price + '</span>';
    this.mobilePriceEl.innerHTML = html;
    this._triggerFade(this.mobilePriceEl);
  };

  /**
   * Update the mobile-only USP tag row shown under the title.
   * Pulls from the active product's `usps` array (per-block setting, not cf-aware).
   */
  ProductCarousel3D.prototype._updateMobileUsps = function (idx) {
    if (!this.mobileUspsEl) return;
    var data = this.ratings[idx];
    if (!data) return;
    var items = data.usps || [];
    var iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">'
      + '<rect width="16" height="16" rx="8" fill="#22B822"/>'
      + '<path d="M4.66699 8H11.3337" stroke="white" stroke-width="1.33333"/>'
      + '<path d="M8 4.66669V11.3334" stroke="white" stroke-width="1.33333"/>'
      + '</svg>';
    var html = '';
    for (var i = 0; i < items.length; i++) {
      var label = document.createElement('span');
      label.textContent = items[i];
      html += '<span class="pc3d-stats__usp">' + iconSvg + label.outerHTML + '</span>';
    }
    this.mobileUspsEl.innerHTML = html;
    this._triggerFade(this.mobileUspsEl);
  };

  /**
   * Update stat bars with given data object
   */
  ProductCarousel3D.prototype._updateBars = function (data) {
    for (var b = 0; b < this.barKeys.length; b++) {
      var val = data[this.barKeys[b]] || 0;
      if (this.barValues[b]) this.barValues[b].innerHTML = val + '<span class="pc3d-bar__max">/6</span>';
      if (this.bars[b]) {
        var blocks = this.bars[b].querySelectorAll('[data-bar-block]');
        for (var bl = 0; bl < blocks.length; bl++) {
          blocks[bl].classList.toggle('is-filled', bl < val);
        }
      }
    }
  };

  /**
   * Update card transforms — only positions visible cards (the ones currently
   * in the ring). Hidden cards (CF mode, blocks without a CF alternative) are
   * left alone; CSS handles their invisibility. Active class is set on the
   * card whose ORIGINAL index matches `this.currentIndex`.
   */
  ProductCarousel3D.prototype._updateCardTransforms = function (ringRotation) {
    for (var v = 0; v < this.visibleCards.length; v++) {
      var card = this.visibleCards[v];
      var theta = this.angle * v;
      var counterRotation = -(ringRotation + theta);
      card.style.transform =
        'rotateY(' + theta + 'deg) translateZ(' + this.radius + 'px) rotateY(' + counterRotation + 'deg)';

      // Depth: calculate how far from front (0° = front, 180° = back)
      var relativeAngle = (theta + ringRotation) % 360;
      if (relativeAngle < 0) relativeAngle += 360;
      var distFromFront = relativeAngle > 180 ? 360 - relativeAngle : relativeAngle;

      // Opacity: front = 1.0, sides = softer, back = nearly invisible
      var t = distFromFront / 180;
      var opacity = t < 0.3 ? 1 : 1 - Math.pow((t - 0.3) / 0.7, 0.6) * 0.85;
      card.style.opacity = Math.max(opacity, 0.1).toFixed(2);
    }

    // Active class + ARIA — set on the original-index card so assistive tech
    // knows which product is currently focused.
    for (var i = 0; i < this.allCards.length; i++) {
      var isActive = i === this.currentIndex;
      this.allCards[i].classList.toggle('is-active', isActive);
      if (isActive) {
        this.allCards[i].setAttribute('aria-current', 'true');
      } else {
        this.allCards[i].removeAttribute('aria-current');
      }
    }
  };

  /**
   * Helper: find the original index of the next/previous visible card,
   * wrapping around the visible-cards list.
   */
  ProductCarousel3D.prototype._neighborOrigIdx = function (step) {
    if (!this.visibleCards.length) return this.currentIndex;
    var curCard = this.allCards[this.currentIndex];
    var curVis = this.visibleCards.indexOf(curCard);
    if (curVis === -1) curVis = 0;
    var newVis = (curVis + step + this.count) % this.count;
    return this.allCards.indexOf(this.visibleCards[newVis]);
  };

  /**
   * Rotate to the given index. `direction` controls how the rotation animates:
   *   'next' — always spin forward by one slot (used for next() and forward swipes)
   *   'prev' — always spin backward by one slot (used for prev() and backward swipes)
   *   undefined — pick the shortest visual path (used for thumb / dot clicks)
   *
   * Rotation is tracked cumulatively (never normalised to 0..360), so wrapping
   * from the last card to the first continues the spin in the same direction
   * instead of unwinding all the way back.
   *
   * When the resolved index equals the current one (e.g. drag snap-back without a
   * swipe), only the ring transform is reapplied — content + fade animations are
   * skipped so the layout doesn't re-trigger for nothing.
   */
  ProductCarousel3D.prototype._rotate = function (index, direction) {
    if (!this.visibleCards.length) return;

    var prevIndex = this.currentIndex;
    var newIndex = ((index % this.allCards.length) + this.allCards.length) % this.allCards.length;

    // If target is hidden (e.g. caller passed an original index that's filtered out
    // in CF mode), snap to the nearest visible card instead.
    if (this.visibleCards.indexOf(this.allCards[newIndex]) === -1) {
      newIndex = this.allCards.indexOf(this.visibleCards[0]);
    }

    var indexChanged = (newIndex !== prevIndex) || !this._initialised;

    // Compute rotation delta in visible-space (visible-angle * step count)
    var prevVisible = this.visibleCards.indexOf(this.allCards[prevIndex]);
    var newVisible = this.visibleCards.indexOf(this.allCards[newIndex]);
    if (prevVisible === -1) prevVisible = 0;

    var delta;
    if (!this._initialised || this.count <= 2) {
      // Initial render, mode switch, or any small ring (count ≤ 2) — snap directly
      // to the target angle. With only 2 cards the ring isn't a true loop, so
      // accumulating delta would drift the rotation off-position after each click.
      this.currentRotation = -this.angle * Math.max(newVisible, 0);
      delta = 0;
    } else if (direction === 'next') {
      delta = -this.angle;
    } else if (direction === 'prev') {
      delta = this.angle;
    } else {
      var diff = newVisible - prevVisible;
      if (diff > this.count / 2) diff -= this.count;
      if (diff < -this.count / 2) diff += this.count;
      delta = -this.angle * diff;
    }

    this.currentIndex = newIndex;
    this.currentRotation += delta;
    this.ring.style.transform =
      'translateZ(-' + this.radius + 'px) rotateY(' + this.currentRotation + 'deg)';

    this._updateCardTransforms(this.currentRotation);

    // Active classes are cheap and need to be in sync even on initial render.
    // All indexed by ORIGINAL position so they line up with cards/thumbs/panels in the markup.
    for (var j = 0; j < this.dots.length; j++) {
      this.dots[j].classList.toggle('is-active', j === this.currentIndex);
    }
    for (var t = 0; t < this.thumbs.length; t++) {
      var thumbActive = t === this.currentIndex;
      this.thumbs[t].classList.toggle('is-active', thumbActive);
      // aria-current ipv aria-selected — pc3d-thumbs zijn <button> zonder
      // role="tab", waar aria-selected ongeldig is. aria-current="true" op de
      // actieve thumb is de juiste 'current item in a set' semantic.
      if (thumbActive) {
        this.thumbs[t].setAttribute('aria-current', 'true');
      } else {
        this.thumbs[t].removeAttribute('aria-current');
      }
    }

    // Drag snap-back to the same index — bail out before any fade or content rebuild
    if (!indexChanged) return;
    this._initialised = true;

    var self = this;
    var currentIdx = this.currentIndex;

    // Lock container height to prevent collapse during the panel crossfade
    if (this.infoInner) {
      this.infoInner.style.height = this.infoInner.offsetHeight + 'px';
    }

    // Fade out all panels
    for (var k = 0; k < this.infoPanels.length; k++) {
      this.infoPanels[k].classList.remove('is-active');
    }

    // Crossfade: fade in the new panel on the next frame (no wait), so the CTA
    // button arrives in sync with the mobile title / USPs / price chip animations.
    if (this._infoPanelTimer) clearTimeout(this._infoPanelTimer);
    this._infoPanelTimer = setTimeout(function () {
      if (self.infoPanels[currentIdx]) {
        self.infoPanels[currentIdx].classList.add('is-active');
        if (self.infoInner) {
          var panel = self.infoPanels[currentIdx];
          var newHeight = panel.scrollHeight;
          self.infoInner.style.height = newHeight + 'px';
        }
      }
    }, 0);

    // Update bar blocks and values (respect section-level cf mode)
    if (this.ratings.length > this.currentIndex) {
      var data = this.ratings[this.currentIndex];
      var statsData = (this.cfMode && data.cf) ? data.cf : data;
      this._updateBars(statsData);
    }

    this._updateMobileTitle(this.currentIndex);
    this._updateMobileUsps(this.currentIndex);
    this._updateMobilePrice(this.currentIndex);
  };

  ProductCarousel3D.prototype.prev = function () {
    this._rotate(this._neighborOrigIdx(-1), 'prev');
    this._resetAutoplay();
  };

  ProductCarousel3D.prototype.next = function () {
    this._rotate(this._neighborOrigIdx(1), 'next');
    this._resetAutoplay();
  };

  ProductCarousel3D.prototype.goTo = function (index) {
    this._rotate(index);
    this._resetAutoplay();
  };

  ProductCarousel3D.prototype._bindEvents = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () { self.prev(); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () { self.next(); });
    }

    for (var i = 0; i < this.dots.length; i++) {
      (function (idx) {
        self.dots[idx].addEventListener('click', function () { self.goTo(idx); });
      })(i);
    }

    // Thumbnail clicks
    for (var t = 0; t < this.thumbs.length; t++) {
      (function (idx) {
        self.thumbs[idx].addEventListener('click', function () { self.goTo(idx); });
      })(t);
    }

    // Card clicks — clicking a side card scrolls the carousel to it.
    // Browsers only fire `click` when there was no significant drag, so a real
    // swipe never reaches this handler. Skip if the card is hidden by CF mode.
    for (var c = 0; c < this.cards.length; c++) {
      (function (idx) {
        self.cards[idx].addEventListener('click', function () {
          if (idx === self.currentIndex) return;
          if (self.visibleCards.indexOf(self.cards[idx]) === -1) return;
          self.goTo(idx);
        });
      })(c);
    }

    this.el.setAttribute('tabindex', '0');
    this.el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { self.prev(); e.preventDefault(); }
      if (e.key === 'ArrowRight') { self.next(); e.preventDefault(); }
    });

    // Mouse drag
    this.scene.addEventListener('mousedown', function (e) {
      e.preventDefault();
      self._onDragStart(e);
    });
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);

    // Touch drag
    this.scene.addEventListener('touchstart', function (e) { self._onDragStart(e); }, { passive: true });
    document.addEventListener('touchmove', this._onTouchMove, { passive: false });
    document.addEventListener('touchend', this._onTouchEnd);

    // Pause autoplay on hover
    this.el.addEventListener('mouseenter', function () { self._stopAutoplay(); });
    this.el.addEventListener('mouseleave', function () {
      if (self.autoplay) self._startAutoplay();
    });

    // Recompute ring radius when the viewport changes (rotation, devtools toggle).
    window.addEventListener('resize', this._onResize);
  };

  ProductCarousel3D.prototype._onDragStart = function (e) {
    this.isDragging = true;
    this.startX = e.type.indexOf('touch') !== -1 ? e.touches[0].clientX : e.clientX;
    this.dragRotation = this.currentRotation;
    this.ring.classList.add('is-dragging');

    // Disable transition on the price chip so it tracks the finger 1:1 during drag
    if (this.mobilePriceEl) {
      this.mobilePriceEl.style.transition = 'none';
    }
  };

  ProductCarousel3D.prototype._onDragMove = function (e) {
    if (!this.isDragging) return;
    var clientX = e.type.indexOf('touch') !== -1 ? e.touches[0].clientX : e.clientX;
    var deltaX = clientX - this.startX;
    var rotDelta = deltaX / 3;
    var effectiveRotation = this.dragRotation + rotDelta;
    this.ring.style.transform =
      'translateZ(-' + this.radius + 'px) rotateY(' + effectiveRotation + 'deg)';
    this._updateCardTransforms(effectiveRotation);

    // Subtle parallax: chip follows the drag at ~15% of the finger movement.
    // Skipped when the user prefers reduced motion.
    if (this.mobilePriceEl && !this.reducedMotion) {
      this.mobilePriceEl.style.transform = 'translateX(' + (deltaX * 0.15) + 'px)';
    }

    if (e.cancelable) e.preventDefault();
  };

  ProductCarousel3D.prototype._onDragEnd = function (e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.ring.classList.remove('is-dragging');

    var clientX;
    if (e.type.indexOf('touch') !== -1) {
      clientX = e.changedTouches ? e.changedTouches[0].clientX : this.startX;
    } else {
      clientX = e.clientX;
    }
    var deltaX = clientX - this.startX;

    // Snap chip back to centre with the CSS transition restored
    if (this.mobilePriceEl) {
      this.mobilePriceEl.style.transition = '';
      this.mobilePriceEl.style.transform = 'translateX(0)';
    }

    if (Math.abs(deltaX) > 40) {
      deltaX < 0 ? this.next() : this.prev();
    } else {
      this._rotate(this.currentIndex);
    }
  };

  ProductCarousel3D.prototype._startAutoplay = function () {
    // Honour reduced-motion preference: don't auto-spawn rotations
    if (this.reducedMotion) return;
    var self = this;
    this._stopAutoplay();
    this.autoplayTimer = setInterval(function () { self.next(); }, this.autoplayDelay);
  };

  ProductCarousel3D.prototype._stopAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  ProductCarousel3D.prototype._resetAutoplay = function () {
    this._stopAutoplay();
    if (this.autoplay) this._startAutoplay();
  };

  ProductCarousel3D.prototype.destroy = function () {
    this._stopAutoplay();
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    document.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('touchend', this._onTouchEnd);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    if (this._infoPanelTimer) clearTimeout(this._infoPanelTimer);
    if (this._modeFadeOutTimer) clearTimeout(this._modeFadeOutTimer);
  };

  // --- Init ---

  function initCarousel(container) {
    var el = container.querySelector
      ? container.querySelector('[data-product-carousel-3d]')
      : null;
    if (!el && container.matches && container.matches('[data-product-carousel-3d]')) el = container;
    if (!el) return;
    if (el[INSTANCE_KEY]) el[INSTANCE_KEY].destroy();
    el[INSTANCE_KEY] = new ProductCarousel3D(el);
  }

  function initAll() {
    var els = document.querySelectorAll('[data-product-carousel-3d]');
    for (var i = 0; i < els.length; i++) {
      if (!els[i][INSTANCE_KEY]) {
        els[i][INSTANCE_KEY] = new ProductCarousel3D(els[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initCarousel(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initCarousel(e.target); });

  // --- Floating CTA ---

  function initFloatingCta() {
    var btns = document.querySelectorAll('[data-pc3d-floating-cta]');
    btns.forEach(function (btn) {
      var sectionId = btn.dataset.pc3dTarget;
      var section = document.getElementById('shopify-section-' + sectionId);
      if (!section) section = document.querySelector('[data-product-carousel-3d]');
      if (!section) { btn.style.display = 'none'; return; }

      // Smooth scroll on click
      btn.addEventListener('click', function () {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Hide when section is in view
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          btn.classList.toggle('is-hidden', entry.isIntersecting);
        });
      }, { threshold: 0.15 });

      observer.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFloatingCta);
  } else {
    initFloatingCta();
  }
})();
